// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ThemeProvider } from './components/ThemeProvider';
import { NotificationsProvider } from './notifications/NotificationsProvider';
import { UserProvider, useUser } from './contexts/UserContext';
import { RoleRouteGuard } from './components/RouteGuard';
import {
  getUserRole,
  ACCOUNTING_ROLES,
  canViewUsers,
  canViewCustomers,
  canViewCarriers,
  canViewPayments,
  canViewLoads,
  canCreateLoads,
  canEditLoads,
  canViewLoadHistory
} from './utils/roleAccess';

// Pages
import { Dashboard } from './pages/Dashboard';
import LoginPage from './pages/login-page/login-form';
import ForgotPassword from './pages/login-page/ForgotPassword';
import VerificationCode from './pages/login-page/verification-code';
import ResetPassword from './pages/login-page/reset-password';
import LoadsPage from './pages/loads-page';
import LoadsAddPage from './pages/loads-page/LoadsAddPage';
import LoadsEditPage from './pages/loads-page/LoadsEditPage';
import LoadHistoryPage from './pages/loads-page/LoadHistoryPage';
import LoadHistoryListPage from './pages/loads-page/LoadHistoryListPage';
import UsersPage from './pages/users-page';
import CustomersPage from './pages/customers-page';
import CarriersPage from './pages/carriers-page';
import StatisticPage from './pages/statistic-page';
import DashboardStats from './pages/DashboardStats';
import UserAudit from './pages/UserAudit';
import Error404 from './pages/Error404';
import PaymentsPage from './pages/payments-page';
import AdminLayout from './components/Layout/AdminLayout';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/theme.css';
import './styles/global.scss';
import './styles/layout.css';
import './styles/components.scss';

const publicRoutes = ['/login', '/forgot-password', '/verification-code', '/reset-password'];

const DefaultRedirect = () => {
  const { user } = useUser();
  const role = getUserRole(user);
  const to = role && ACCOUNTING_ROLES.includes(role) ? '/payments' : '/loads';
  return <Navigate to={to} replace />;
};

const AuthWatcher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser, clearUser, isTokenExpired } = useUser();

  useEffect(() => {
    const isPublic = publicRoutes.some((route) => location.pathname.startsWith(route));
    const token = localStorage.getItem('authToken');

    if (!token) {
      if (!isPublic) {
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }
      return;
    }

    if (isTokenExpired(token)) {
      clearUser();
      if (!isPublic) {
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }
      return;
    }

    if (!isPublic) {
      refreshUser();
    }
  }, [location.pathname, navigate, refreshUser, clearUser, isTokenExpired]);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <NotificationsProvider>
          <Router>
            <AuthWatcher />
            <div className="App">
              <Routes>
            {/* Authentication Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verification-code" element={<VerificationCode />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Default Route */}
            <Route path="/" element={<DefaultRedirect />} />

            {/* Protected Routes with Layout */}
            <Route path="/dashboard" element={
              <AdminLayout>
                <Dashboard />
              </AdminLayout>
            } />
            <Route path="/loads" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewLoads}>
                  <LoadsPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/loads/add" element={
              <AdminLayout>
                <RoleRouteGuard check={(role) => canViewLoads(role) && canCreateLoads(role)}>
                  <LoadsAddPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/loads/edit/:id" element={
              <AdminLayout>
                <RoleRouteGuard check={(role) => canViewLoads(role) && canEditLoads(role)}>
                  <LoadsEditPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/loads/history" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewLoadHistory}>
                  <LoadHistoryListPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/loads/history/:id" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewLoadHistory}>
                  <LoadHistoryPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/users" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewUsers}>
                  <UsersPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/customers" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewCustomers}>
                  <CustomersPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/carriers" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewCarriers}>
                  <CarriersPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/payments" element={
              <AdminLayout>
                <RoleRouteGuard check={canViewPayments}>
                  <PaymentsPage />
                </RoleRouteGuard>
              </AdminLayout>
            } />
            <Route path="/statistics" element={
              <AdminLayout>
                <StatisticPage/>
              </AdminLayout>
            } />
            <Route path="/stats" element={
              <AdminLayout>
                <DashboardStats/>
              </AdminLayout>
            } />
            <Route path="/audit/user/:userId" element={
              <AdminLayout>
                <UserAudit/>
              </AdminLayout>
            } />
            
            {/* Error 404 Route */}
            <Route path="/error404" element={<Error404 />} />

            {/* Catch all route - redirect to 404 */}
            <Route path="*" element={<Error404 />} />
              </Routes>
            </div>
          </Router>
        </NotificationsProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
